import { PricingPlan, UserProfile } from '../types/user';

const PREMIUM_WEEKLY_LIMIT = 7;
const BASIC_WEEKLY_LIMIT = 1;
const FREE_MONTHLY_LIMIT = 1;
const WEEK_WINDOW_IN_DAYS = 7;
const MS_IN_DAY = 1000 * 60 * 60 * 24;

const INITIAL_CREDITS: Record<PricingPlan, number> = {
  free: 2,
  basic: 4,
  premium: 0,
};

const getPlanInitialCredits = (plan: PricingPlan) => INITIAL_CREDITS[plan] ?? 0;

const isNewMonth = (lastReset?: Date, now: Date = new Date()) => {
  if (!lastReset) return true;
  return lastReset.getFullYear() !== now.getFullYear() || lastReset.getMonth() !== now.getMonth();
};

const hasElapsedDays = (lastReset?: Date, now: Date = new Date(), days = WEEK_WINDOW_IN_DAYS) => {
  if (!lastReset) return true;
  const diffDays = (now.getTime() - lastReset.getTime()) / MS_IN_DAY;
  return diffDays >= days;
};

const monthsBetween = (from?: Date, to: Date = new Date()) => {
  if (!from) return 0;
  return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
};

interface InitialCreditState {
  remaining: number;
  assignedAt?: Date;
  planInitialCredits: number;
  configured: boolean;
  planMatches: boolean;
}

const resolveInitialCreditState = (userProfile: UserProfile): InitialCreditState => {
  const planInitialCredits = getPlanInitialCredits(userProfile.plan);
  const planMatches = userProfile.initialCreditsPlan === userProfile.plan;
  const assignedAtStr = planMatches ? userProfile.initialCreditsAssignedAt : undefined;
  const assignedAt = assignedAtStr ? new Date(assignedAtStr) : undefined;

  let remaining = planInitialCredits;
  if (planMatches && typeof userProfile.initialCreditsRemaining === 'number') {
    remaining = userProfile.initialCreditsRemaining;
  }

  return {
    remaining,
    assignedAt,
    planInitialCredits,
    configured: Boolean(assignedAtStr),
    planMatches,
  };
};

const hasFreePlanMonthlyAccess = (state: InitialCreditState, now: Date) => {
  // Free monthly cadence available starting the month after initial credits assignment
  if (!state.assignedAt) {
    return false;
  }
  return monthsBetween(state.assignedAt, now) >= 1;
};

/**
 * Vérifie si l'utilisateur a atteint sa limite de rêves selon son plan d'abonnement
 */
export const checkDreamLimit = (userProfile: UserProfile): { canCreateDream: boolean; reason?: string } => {
  // Exception pour les administrateurs : pas de limite
  if (userProfile.isAdmin) {
    return { canCreateDream: true };
  }

  const now = new Date();
  const initialState = resolveInitialCreditState(userProfile);

  if (initialState.remaining > 0) {
    return { canCreateDream: true };
  }

  if (userProfile.plan === 'free') {
    if (!hasFreePlanMonthlyAccess(initialState, now)) {
      return { canCreateDream: false, reason: 'free_initial_period' };
    }

    const lastReset = userProfile.lastDreamCountReset ? new Date(userProfile.lastDreamCountReset) : undefined;
    if (isNewMonth(lastReset, now)) {
      return { canCreateDream: true };
    }

    if ((userProfile.monthlyDreamCount || 0) >= FREE_MONTHLY_LIMIT) {
      return { canCreateDream: false, reason: 'free_monthly_limit_reached' };
    }

    return { canCreateDream: true };
  }

  if (userProfile.plan === 'basic') {
    const lastWeeklyReset = userProfile.lastWeeklyCountReset ? new Date(userProfile.lastWeeklyCountReset) : undefined;

    if (hasElapsedDays(lastWeeklyReset, now)) {
      return { canCreateDream: true };
    }

    if ((userProfile.weeklyDreamCount || 0) >= BASIC_WEEKLY_LIMIT) {
      return { canCreateDream: false, reason: 'basic_weekly_limit_reached' };
    }

    return { canCreateDream: true };
  }

  if (userProfile.plan === 'premium' && Number.isFinite(PREMIUM_WEEKLY_LIMIT)) {
    const lastWeeklyReset = userProfile.lastWeeklyCountReset ? new Date(userProfile.lastWeeklyCountReset) : undefined;

    if (hasElapsedDays(lastWeeklyReset, now)) {
      return { canCreateDream: true };
    }

    if ((userProfile.weeklyDreamCount || 0) >= PREMIUM_WEEKLY_LIMIT) {
      return {
        canCreateDream: false,
        reason: 'premium_weekly_limit_reached',
      };
    }
  }

  return { canCreateDream: true };
};

/**
 * Met à jour les compteurs de rêves dans le profil utilisateur
 */
export const updateDreamCounters = (userProfile: UserProfile): Partial<UserProfile> => {
  const now = new Date();
  const updates: Partial<UserProfile> = {};

  updates.dreamCount = (userProfile.dreamCount || 0) + 1;

  const initialState = resolveInitialCreditState(userProfile);
  let usedInitialCredit = false;

  if (initialState.planInitialCredits > 0) {
    const baselineRemaining = initialState.planMatches ? initialState.remaining : initialState.planInitialCredits;

    if (!initialState.planMatches || !initialState.configured) {
      usedInitialCredit = true;
      updates.initialCreditsPlan = userProfile.plan;
      const assignedAtIso = !initialState.planMatches
        ? now.toISOString()
        : userProfile.initialCreditsAssignedAt || userProfile.registrationDate || now.toISOString();
      updates.initialCreditsAssignedAt = assignedAtIso;
      updates.initialCreditsRemaining = Math.max(baselineRemaining - 1, 0);
    } else if (baselineRemaining > 0) {
      usedInitialCredit = true;
      updates.initialCreditsRemaining = Math.max(baselineRemaining - 1, 0);
    }
  }

  if (userProfile.plan === 'free' && !usedInitialCredit) {
    const lastReset = userProfile.lastDreamCountReset ? new Date(userProfile.lastDreamCountReset) : undefined;
    if (isNewMonth(lastReset, now)) {
      updates.monthlyDreamCount = 1;
      updates.lastDreamCountReset = now.toISOString();
    } else {
      updates.monthlyDreamCount = (userProfile.monthlyDreamCount || 0) + 1;
    }
  }

  const shouldTrackWeekly =
    (userProfile.plan === 'basic' && !usedInitialCredit) || userProfile.plan === 'premium';

  if (shouldTrackWeekly) {
    const lastWeeklyReset = userProfile.lastWeeklyCountReset ? new Date(userProfile.lastWeeklyCountReset) : undefined;
    if (hasElapsedDays(lastWeeklyReset, now)) {
      updates.weeklyDreamCount = 1;
      updates.lastWeeklyCountReset = now.toISOString();
    } else {
      updates.weeklyDreamCount = (userProfile.weeklyDreamCount || 0) + 1;
    }
  }

  return updates;
};

/**
 * Calcule le nombre d'analyses restantes selon le plan de l'utilisateur
 */
export const getRemainingAnalyses = (userProfile: UserProfile): string | number => {
  // Admin : analyses illimitées
  if (userProfile.isAdmin) {
    return '∞';
  }

  const now = new Date();
  const initialState = resolveInitialCreditState(userProfile);

  if (initialState.remaining > 0) {
    return initialState.remaining;
  }

  if (userProfile.plan === 'free') {
    if (!hasFreePlanMonthlyAccess(initialState, now)) {
      return 0;
    }

    const lastReset = userProfile.lastDreamCountReset ? new Date(userProfile.lastDreamCountReset) : undefined;
    if (isNewMonth(lastReset, now)) {
      return FREE_MONTHLY_LIMIT;
    }

    return Math.max(0, FREE_MONTHLY_LIMIT - (userProfile.monthlyDreamCount || 0));
  }

  if (userProfile.plan === 'basic') {
    const lastWeeklyReset = userProfile.lastWeeklyCountReset ? new Date(userProfile.lastWeeklyCountReset) : undefined;

    if (hasElapsedDays(lastWeeklyReset, now)) {
      return BASIC_WEEKLY_LIMIT;
    }

    return Math.max(0, BASIC_WEEKLY_LIMIT - (userProfile.weeklyDreamCount || 0));
  }

  if (userProfile.plan === 'premium') {
    const lastWeeklyReset = userProfile.lastWeeklyCountReset ? new Date(userProfile.lastWeeklyCountReset) : undefined;

    if (hasElapsedDays(lastWeeklyReset, now)) {
      return PREMIUM_WEEKLY_LIMIT;
    }

    return Math.max(0, PREMIUM_WEEKLY_LIMIT - (userProfile.weeklyDreamCount || 0));
  }

  return 0;
};
